import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  Typography,
  useTheme
} from "@mui/material";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useTesterIdentity } from "./TesterIdentityProvider";
import { getPublicTestingDoc } from "./api";

const TestPlanViewerDialog = ({ open, onClose, docId, title }) => {
  const { getIdToken } = useTesterIdentity();
  const { palette } = useTheme();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !docId) {
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setDoc(null);
    getPublicTestingDoc({ docId, getIdToken })
      .then((result) => {
        if (cancelled) return;
        setDoc(result);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || "Failed to load document");
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, docId, getIdToken]);

  const displayTitle = doc?.title || title || "Test plan";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
    >
      <DialogTitle>{displayTitle}</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <CircularProgress size={24} />
          </Box>
        ) : null}
        {error ? (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        ) : null}
        {doc && !loading && !error ? (
          <Box
            sx={{
              "& h1, & h2, & h3, & h4": { mt: 2, mb: 1 },
              "& p, & li": { lineHeight: 1.55 },
              "& code": {
                bgcolor: palette.backgroundGray.light,
                px: 0.5,
                borderRadius: 0.5
              },
              "& pre": {
                bgcolor: palette.backgroundGray.light,
                p: 1.5,
                borderRadius: 1,
                overflowX: "auto"
              },
              "& blockquote": {
                borderLeft: `3px solid ${palette.backgroundGray.dark}`,
                pl: 2,
                color: palette.text.secondary,
                my: 1
              },
              "& table": { borderCollapse: "collapse", my: 1 },
              "& th, & td": {
                border: `1px solid ${palette.backgroundGray.dark}`,
                px: 1,
                py: 0.5
              }
            }}
          >
            <Markdown remarkPlugins={[remarkGfm]}>{doc.body}</Markdown>
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TestPlanViewerDialog;
